import axios from "axios";
import api from "./axios";

const EXTERNAL_TOKEN_KEY = "external_api_token";
const BASE_URL = process.env.REACT_APP_EXTERNAL_API_BASE_URL;

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

async function fetchExternalToken() {
  const response = await api.get("/auth/get-external-token");
  const token = response.data.access_token;
  localStorage.setItem(EXTERNAL_TOKEN_KEY, token);
  return token;
}

async function getToken() {
  const token = localStorage.getItem(EXTERNAL_TOKEN_KEY);
  if (token) return token;
  return fetchExternalToken();
}

const externalApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor — attach token
externalApi.interceptors.request.use(async (config) => {
  const token = await getToken();
  config.headers["auth-token"] = token;
  return config;
});

// Response interceptor — refresh on 401/403
externalApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          localStorage.removeItem(EXTERNAL_TOKEN_KEY);
          const newToken = await fetchExternalToken();
          isRefreshing = false;
          onRefreshed(newToken);

          originalRequest.headers["auth-token"] = newToken;
          return externalApi(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          localStorage.removeItem(EXTERNAL_TOKEN_KEY);
          return Promise.reject(refreshError);
        }
      }

      // If refresh already in progress, queue this request
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken) => {
          originalRequest._retry = true;
          originalRequest.headers["auth-token"] = newToken;
          resolve(externalApi(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

export default externalApi;
